import { accent } from "../hooks/tokens.js";
import { MTU_OPTS, parseCidr, n2ip, maskOf, scopeOf, prefixFor } from "../data.js";

// Boîte à outils : sous-réseau/VLSM/MTU. Trois calculatrices indépendantes,
// chacune purement dérivée de s.cidrInput / s.vlsmBase+vlsmNeeds / s.mtuSel.
export function buildToolsView(ctx) {
  const { s, patch } = ctx;

  return {
    calcTabs: [{ k: "subnet", label: "Sous-réseau" }, { k: "vlsm", label: "Découpage VLSM" }, { k: "mtu", label: "MTU & overhead" }]
      .map((t) => ({ label: t.label,
        color: s.calcTab === t.k ? "oklch(0.24 0.012 60)" : "oklch(0.55 0.015 60)",
        line: s.calcTab === t.k ? accent : "transparent",
        onClick: () => patch({ calcTab: t.k, splitPrefix: null }) })),
    isCalcSubnet: s.calcTab === "subnet", isCalcVlsm: s.calcTab === "vlsm", isCalcMtu: s.calcTab === "mtu",
    cidrInput: s.cidrInput,
    onCidr: (e) => patch({ cidrInput: e.target.value, splitPrefix: null }),
    ...(() => {
      const c = parseCidr(s.cidrInput);
      if (!c) return { cidrHint: "Format attendu : adresse/préfixe, par exemple 10.20.30.5/22.", cidrHintColor: "oklch(0.5 0.09 30)",
        subnetRows: [], splitOptions: [], splitRows: [], splitNote: "" };
      const mask = maskOf(c.p), size = Math.pow(2, 32 - c.p);
      const net = Math.floor(c.ip / size) * size, bcast = net + size - 1;
      const usable = c.p >= 31 ? (c.p === 31 ? 2 : 1) : size - 2;
      const first = c.p >= 31 ? net : net + 1, last = c.p >= 31 ? bcast : bcast - 1;
      const sp = s.splitPrefix && s.splitPrefix > c.p ? s.splitPrefix : Math.min(32, c.p + 2);
      const subSize = Math.pow(2, 32 - sp), count = size / subSize;
      const subUsable = sp >= 31 ? (sp === 31 ? 2 : 1) : subSize - 2;
      return {
        cidrHint: scopeOf(net) + " · bloc de " + size.toLocaleString("fr-FR") + " adresses",
        cidrHintColor: "oklch(0.55 0.015 60)",
        subnetRows: [
          { label: "Réseau", value: n2ip(net) + "/" + c.p },
          { label: "Masque", value: n2ip(mask) },
          { label: "Wildcard", value: n2ip(4294967295 - mask) },
          { label: "Diffusion", value: n2ip(bcast) },
          { label: "Première utilisable", value: n2ip(first) },
          { label: "Dernière utilisable", value: n2ip(last) },
          { label: "Hôtes utilisables", value: usable.toLocaleString("fr-FR") },
          { label: "Bloc suivant", value: n2ip(net + size) + "/" + c.p },
        ],
        splitOptions: [c.p + 1, c.p + 2, c.p + 3, c.p + 4].filter((x) => x <= 32).map((x) => ({
          label: "/" + x, border: x === sp ? accent : "oklch(0.92 0.008 85)",
          color: x === sp ? accent : "oklch(0.5 0.015 60)",
          onClick: () => patch({ splitPrefix: x }),
        })),
        splitRows: Array.from({ length: Math.min(6, count) }, (_, i) => ({
          n: String(i + 1), net: n2ip(net + i * subSize) + "/" + sp,
          usable: subUsable.toLocaleString("fr-FR") + " hôtes",
        })),
        splitNote: count.toLocaleString("fr-FR") + " sous-réseaux /" + sp + " dans ce bloc" + (count > 6 ? ", six premiers affichés." : "."),
      };
    })(),
    vlsmBase: s.vlsmBase, vlsmNeeds: s.vlsmNeeds,
    onVlsmBase: (e) => patch({ vlsmBase: e.target.value }),
    onVlsmNeeds: (e) => patch({ vlsmNeeds: e.target.value }),
    ...(() => {
      const base = parseCidr(s.vlsmBase);
      const needs = s.vlsmNeeds.split(/[,;\s]+/).map((x) => parseInt(x, 10)).filter((x) => !isNaN(x) && x > 0);
      if (!base || !needs.length) return { vlsmRows: [], vlsmNote: "Renseignez un bloc valide et au moins un besoin en hôtes." };
      const size = Math.pow(2, 32 - base.p);
      const start = Math.floor(base.ip / size) * size, end = start + size;
      let cur = start, used = 0;
      const rows = needs.slice().sort((a, b) => b - a).map((need, i) => {
        const p = prefixFor(need), sz = Math.pow(2, 32 - p);
        const aligned = Math.ceil(cur / sz) * sz;
        if (aligned + sz > end) return { n: String(i + 1), need: need + " hôtes", prefix: "hors bloc", range: "—", slack: "—", color: "oklch(0.5 0.09 30)" };
        cur = aligned + sz; used += sz;
        const u = p >= 31 ? (p === 31 ? 2 : 1) : sz - 2;
        return { n: String(i + 1), need: need + " hôtes", prefix: n2ip(aligned) + "/" + p,
          range: n2ip(p >= 31 ? aligned : aligned + 1) + " – " + n2ip(p >= 31 ? aligned + sz - 1 : aligned + sz - 2),
          slack: (u - need) + " libres", color: "oklch(0.24 0.012 60)" };
      });
      return { vlsmRows: rows,
        vlsmNote: Math.round(used / size * 100) + " % du bloc consommé · reste " + ((size - used)).toLocaleString("fr-FR") + " adresses, prochain bloc libre à partir de " + n2ip(cur) + ". Prévoyez le doublement avant de découper au plus juste." };
    })(),
    mtuOptions: MTU_OPTS.map((o) => {
      const on = !!s.mtuSel[o.id];
      return { label: o.label, bytes: o.bytes,
        border: on ? accent : "oklch(0.93 0.008 85)", bg: on ? "oklch(0.99 0.006 60)" : "transparent",
        boxBorder: on ? accent : "oklch(0.85 0.01 85)", boxFill: on ? accent : "transparent",
        onClick: () => patch({ mtuSel: { ...s.mtuSel, [o.id]: !on } }) };
    }),
    ...(() => {
      const oh = MTU_OPTS.filter((o) => s.mtuSel[o.id]).reduce((n, o) => n + o.bytes, 0);
      return { mtuResults: [
          { label: "Overhead cumulé", value: "+" + oh + " o", color: accent },
          { label: "Charge utile sur MTU 1500", value: (1500 - oh) + " o", color: "oklch(0.24 0.012 60)" },
          { label: "MSS TCP conseillé", value: (1500 - oh - 40) + " o", color: "oklch(0.24 0.012 60)" },
          { label: "MTU transport requis", value: (1500 + oh) + " o", color: "oklch(0.24 0.012 60)" },
          { label: "Charge utile sur jumbo 9216", value: (9216 - oh) + " o", color: "oklch(0.4 0.015 60)" },
        ],
        mtuNote: oh > 50
          ? "Au-delà de 50 octets d'overhead, un underlay à 1500 ne suffit plus : passez le transport en jumbo ou ajustez le MSS à l'entrée du tunnel."
          : "Un ajustement de MSS à l'entrée évite de dépendre de la découverte de MTU, souvent cassée par le filtrage ICMP." };
    })(),
  };
}
